import { useQuery } from '@tanstack/react-query'
import { fetchLanguages } from '@/api/languagesApi'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/stores/authStore'
import type { ApiLanguage } from '@/types/api'

const LANGUAGES_QUERY = {
  queryKey: ['languages'] as const,
  queryFn: fetchLanguages,
  staleTime: 60 * 60 * 1000,
}

/**
 * First-run language prompt state.
 *
 * The prompt is owed to a user who has never explicitly chosen a content language
 * (`hasLanguagePreference === false`) while the server offers more than one. Until it is answered
 * the content bundle is not fetched, so the very first bundle downloaded is the one for the
 * language the user actually wants rather than the system default.
 *
 * Deliberately not prompted while offline: the languages list is a live call, and an offline
 * cold-start only has the cached bundle for the already-selected language anyway.
 */
export function useLanguagePrompt(): {
  isPrompting: boolean
  languages: ApiLanguage[]
} {
  const isOffline = useAppStore((s) => s.isOffline)
  const resolved = useAppStore((s) => s.languagePromptResolved)
  const currentUser = useAuthStore((s) => s.currentUser)

  // Strict false check: an identity persisted by an older build has no such field, and an
  // unknown answer must not trigger the prompt.
  const owedPrompt = !isOffline && !resolved && currentUser?.hasLanguagePreference === false

  const { data, isError } = useQuery({ ...LANGUAGES_QUERY, enabled: owedPrompt })

  if (!owedPrompt) return { isPrompting: false, languages: [] }

  // A failed languages call must never strand the user behind the overlay.
  if (isError) return { isPrompting: false, languages: [] }

  const languages = data?.languages ?? []
  return { isPrompting: languages.length > 1, languages }
}

/**
 * True while the answer to the language prompt is still outstanding: including the window in
 * which the languages list is still loading, so the content bundle is not fetched in a language
 * the user is about to change. Consumed by `useContentBundle` and the overlay.
 */
export function useLanguagePromptPending(): boolean {
  const isOffline = useAppStore((s) => s.isOffline)
  const resolved = useAppStore((s) => s.languagePromptResolved)
  const currentUser = useAuthStore((s) => s.currentUser)

  const owedPrompt = !isOffline && !resolved && currentUser?.hasLanguagePreference === false

  const { data, isError } = useQuery({ ...LANGUAGES_QUERY, enabled: owedPrompt })

  if (!owedPrompt || isError) return false
  // Undecided (list still in flight) counts as pending: the decision is imminent.
  return !data || data.languages.length > 1
}
